/**
 * mp4 컨테이너에서 첫 번째 오디오 트랙의 코덱(4cc)을 추출하여
 * 브라우저/Remotion이 직접 재생 가능한 코덱인지 판정한다.
 *
 * 서버의 transcode whitelist (`ai-api/common/ffmpeg/transcode.py`)와 일치:
 * - 호환: mp4a (AAC), mp3
 * - 비호환: ec-3, ac-3, alac, opus(in mp4) 등 → 서버에서 AAC로 자동 재인코딩
 *
 * moov atom이 파일 끝에 있는 non-faststart mp4는 헤더 스캔만으론 검출 불가 →
 * codec=null 반환. 이 경우 사용자 경고는 표시하지 않으며, 서버 변환 자체는 정상.
 *
 * mp4 외 컨테이너(webm, mov 일부)는 본 함수 대상이 아님 — 호출부에서 확장자/MIME으로 사전 필터.
 */

const MOOV = 0x6d6f6f76;
const TRAK = 0x7472616b;
const MDIA = 0x6d646961;
const MINF = 0x6d696e66;
const STBL = 0x7374626c;
const HDLR = 0x68646c72;
const STSD = 0x73747364;
const SOUN = 0x736f756e;

const HEADER_SCAN_LIMIT = 5 * 1024 * 1024;

const SUPPORTED_AUDIO_4CC = new Set(['mp4a', 'mp3 ', '.mp3']);

export interface AudioCodecResult {
  /** 4cc 문자열 (e.g. 'mp4a', 'ec-3'). 검출 실패 시 null */
  codec: string | null;
  /** 브라우저 호환 여부. null = 판별 불가 */
  isSupported: boolean | null;
}

export async function detectMp4AudioCodec(
  file: File | Blob,
): Promise<AudioCodecResult> {
  try {
    const sliceSize = Math.min(HEADER_SCAN_LIMIT, file.size);
    const buf = await file.slice(0, sliceSize).arrayBuffer();
    const u8 = new Uint8Array(buf);
    const dv = new DataView(buf);

    const moovStart = findTopLevelBox(dv, u8.length, MOOV);
    if (moovStart < 0) return { codec: null, isSupported: null };

    const moovSize = dv.getUint32(moovStart);
    const moovEnd = Math.min(moovStart + moovSize, u8.length);
    if (moovSize < 8 || moovEnd <= moovStart + 8) {
      return { codec: null, isSupported: null };
    }

    const codec = findAudioCodecInMoov(dv, u8, moovStart + 8, moovEnd);
    if (codec === null) return { codec: null, isSupported: null };

    return { codec, isSupported: SUPPORTED_AUDIO_4CC.has(codec) };
  } catch {
    return { codec: null, isSupported: null };
  }
}

function findTopLevelBox(dv: DataView, totalLen: number, type: number): number {
  let off = 0;
  while (off < totalLen - 8) {
    const size = dv.getUint32(off);
    const t = dv.getUint32(off + 4);
    if (size === 0 || size < 8) return -1;
    if (t === type) return off;
    off += size;
  }
  return -1;
}

function findAudioCodecInMoov(
  dv: DataView,
  u8: Uint8Array,
  start: number,
  end: number,
): string | null {
  let off = start;
  while (off < end - 8) {
    const size = dv.getUint32(off);
    const type = dv.getUint32(off + 4);
    if (size < 8 || off + size > end) return null;
    if (type === TRAK) {
      const codec = parseTrakForAudio(dv, u8, off + 8, off + size);
      if (codec) return codec;
    }
    off += size;
  }
  return null;
}

function parseTrakForAudio(
  dv: DataView,
  u8: Uint8Array,
  start: number,
  end: number,
): string | null {
  let isAudioTrack = false;
  let firstSampleEntry4cc: string | null = null;

  const walk = (off: number, limit: number) => {
    while (off < limit - 8) {
      const size = dv.getUint32(off);
      const type = dv.getUint32(off + 4);
      if (size < 8 || off + size > limit) return;

      if (type === HDLR) {
        // hdlr: version(1) + flags(3) + pre_defined(4) + handler_type(4)
        if (off + 16 + 4 <= limit) {
          const handlerType = dv.getUint32(off + 16);
          if (handlerType === SOUN) isAudioTrack = true;
        }
      } else if (type === STSD) {
        // stsd: version(1) + flags(3) + entry_count(4) + entries...
        // first entry: size(4) + format(4) + ...
        const entryCount = dv.getUint32(off + 12);
        if (entryCount > 0 && off + 24 <= limit) {
          firstSampleEntry4cc = String.fromCharCode(
            u8[off + 20] ?? 0,
            u8[off + 21] ?? 0,
            u8[off + 22] ?? 0,
            u8[off + 23] ?? 0,
          );
        }
      } else if (type === MDIA || type === MINF || type === STBL) {
        walk(off + 8, off + size);
      }
      off += size;
    }
  };

  walk(start, end);
  return isAudioTrack ? firstSampleEntry4cc : null;
}

/**
 * 비디오 파일이 mp4/quicktime 컨테이너인 경우에만 코덱 검사를 수행하고,
 * 비호환 코덱이 감지되면 콜백 호출. 그 외엔 침묵.
 *
 * 다중 파일 동시 업로드(DnD 등)에서 토스트 스팸 방지를 위해
 * 마지막 호출로부터 WARN_COOLDOWN_MS 내 추가 호출은 콜백을 생략한다.
 * (check + set는 await 이후 동기 블록이라 단일 스레드 JS에서 race 없음)
 *
 * 호출부 패턴:
 *   await warnIfUnsupportedAudioCodec(file, () => toast({...}));
 */
const WARN_COOLDOWN_MS = 5000;
let lastWarnedAt = 0;

export async function warnIfUnsupportedAudioCodec(
  file: File,
  onUnsupported: () => void,
): Promise<void> {
  const isMp4Like =
    file.type === 'video/mp4' ||
    file.type === 'video/quicktime' ||
    /\.(mp4|mov|m4v)$/i.test(file.name);
  if (!isMp4Like) return;

  const result = await detectMp4AudioCodec(file);
  if (result.isSupported !== false) return;

  const now = Date.now();
  if (now - lastWarnedAt <= WARN_COOLDOWN_MS) return;
  lastWarnedAt = now;
  onUnsupported();
}
